import { CommonModule } from '@angular/common';
import { ActivatedRoute, ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { AuthFlowType, AuthForm } from '../types/auth-form.type';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  Component,
  computed,
  Inject,
  inject,
  OnInit,
  QueryList,
  signal,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { filter, startWith } from 'rxjs';
import { ToggleComponent } from '../components/toggle/toggle.component';
import { MatStepper, MatStepperModule } from '@angular/material/stepper';
import { SignupFormComponent } from '../components/signup-form/signup-form.component';
import { SigninFormComponent } from '../components/signin-form/signin-form.component';
import { AuthService } from '../services/auth.service';
import { WINDOW } from '../../../core/tokens/window';
import { GaEventService } from '../../../core/services/ga.service';
import { ParentAnalyticsEvents } from '../../../core/analytics/parent-analytics.events';
import { getValvurApiError } from '../../../core/errors/parse-valvur-api-error';
import { UserService } from '../../user/service/user.service';
import { IdentityNavigationService } from '../../identity/services/identity-navigation.service';
import {
  persistPostAuthReturnUrl,
} from '../helpers/post-auth-return.helper';

@Component({
  selector: 'app-auth',
  standalone: true,
  imports: [
    CommonModule,
    TranslatePipe,
    ToggleComponent,
    MatStepperModule,
    SignupFormComponent,
    SigninFormComponent,
  ],
  templateUrl: './auth.component.html',
  styleUrls: ['./auth.component.scss'],
})
export class AuthComponent implements OnInit {
  #authService = inject(AuthService);
  #gaService = inject(GaEventService);
  #router = inject(Router);
  #userService = inject(UserService);
  #identityNav = inject(IdentityNavigationService);
  #layout = signal<string>('parent');
  #route = inject(ActivatedRoute);
  readonly layout = computed(() => this.#layout());

  isPending = computed(
    () =>
      this.#authService.signin.isPending() ||
      this.#authService.signup.isPending()
  );

  isSchool = computed(() => this.layout() === 'school');
  isAdmin = computed(() => this.layout() === 'admin');
  isParentAuthLayout = computed(() => this.layout() === 'parent');
  /** Admin + school: single-card sign-in (no signup), same layout as patrol. */
  isStaffAuthLayout = computed(() => this.isAdmin() || this.isSchool());

  @ViewChild('stepper', { static: false }) private stepper!: MatStepper;
  @ViewChildren(SignupFormComponent)
  private signupForms!: QueryList<SignupFormComponent>;

  private steps: Record<AuthFlowType, number> = {
    signup: 0,
    signin: 1,
  };

  constructor(@Inject(WINDOW) private window: Window) {
    this.initLayout();
  }

  ngOnInit(): void {
    this.captureReturnUrlFromQuery();
    if (this.isParentAuthLayout() && this.#authService.isAuthenticated()) {
      void this.restoreParentSession();
    }
  }

  public handleSignup(
    value: Record<string, string | null | undefined>,
  ): void {
    void this.#authService.signup
      .mutateAsync({ form: value, layout: this.layout() })
      .then(() => {
        this.#gaService.sendEvent(ParentAnalyticsEvents.SIGNUP, {
          method: 'email',
          user_role: this.layout(),
          layout: this.layout(),
        });
      })
      .catch((err: unknown) => {
        const api = getValvurApiError(err);
        const form = this.signupForms?.first;
        if (!api || !form) {
          return;
        }
        if (
          api.errorCode === 'AUTH_USERNAME_INVALID' ||
          api.errorCode === 'AUTH_USER_ALREADY_EXISTS_REGISTER'
        ) {
          form.setUserNameServerError(
            api.message,
            api.meta?.userNameSuggestions ?? [],
          );
        }
      });
  }

  public handleSignin(form: AuthForm): void {
    this.#authService.signin
      .mutateAsync({ form, layout: this.layout() })
      .then(() => {
        this.#gaService.sendEvent(ParentAnalyticsEvents.SIGNIN, {
          method: 'email',
          user_role: this.layout(),
          layout: this.layout(),
        });
      });
  }

  public selectAuthFlow(authFlow: AuthFlowType) {
    this.stepper.selectedIndex = this.steps[authFlow];
  }

  private async restoreParentSession(): Promise<void> {
    try {
      await this.#userService.reloadProfile();
      await this.#identityNav.navigateAfterAuth('parent');
    } catch {
      this.#authService.clearSession();
    }
  }

  private captureReturnUrlFromQuery(): void {
    const returnUrl = this.#route.snapshot.queryParamMap.get('returnUrl');
    if (returnUrl) {
      persistPostAuthReturnUrl(returnUrl);
    }
  }

  private initLayout() {
    this.#router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        startWith(null),
        takeUntilDestroyed(),
      )
      .subscribe(() => {
        const layout = this.readLayoutFromRoute();
        this.#layout.set(layout);
        this.syncBodyAuthClass(layout);
      });
  }

  private readLayoutFromRoute(): string {
    let route: ActivatedRouteSnapshot | null = this.#route.snapshot;

    while (route) {
      const layout = route.data['layout'];
      if (typeof layout === 'string' && layout.length > 0) {
        return layout;
      }
      route = route.parent;
    }

    return 'parent';
  }

  private syncBodyAuthClass(layout: string): void {
    const body = this.window.document.body;
    const root = this.window.document.documentElement;
    body.classList.remove('auth-padding', 'auth-padding--parent');
    root.classList.remove('auth-padding', 'auth-padding--parent');

    if (layout === 'parent') {
      body.classList.add('auth-padding--parent');
      root.classList.add('auth-padding--parent');
      return;
    }

    // Admin/school use a self-contained patrol-style page (no body padding).
    if (layout === 'admin' || layout === 'school') {
      return;
    }

    body.classList.add('auth-padding');
    root.classList.add('auth-padding');
  }

  ngOnDestroy() {
    this.window.document.body.classList.remove(
      'auth-padding',
      'auth-padding--parent',
    );
    this.window.document.documentElement.classList.remove(
      'auth-padding',
      'auth-padding--parent',
    );
  }
}
